// Portal-scoped translations. English + Hindi for every visible string
// in /portal/* pages. Keep keys descriptive and flat — no nested objects.
//
// Lookup convention: t('claims.title') → English or Hindi based on active lang.
// Missing keys fall back to the English value, then to the key string itself
// (loud signal during development that a string isn't translated yet).
//
// When you add a new UI string, add it here under both `en` and `hi`. If a
// Hindi translation is genuinely unavailable, leave it empty and the runtime
// will gracefully fall back to English.

export const TRANSLATIONS = {
  en: {
    // ── Common ─────────────────────────────────────────────────────────
    'common.loading': 'Loading…',
    'common.save': 'Save',
    'common.saving': 'Saving…',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.remove': 'Remove',
    'common.upload': 'Upload',
    'common.uploading': 'Uploading…',
    'common.preview': 'Preview',
    'common.download': 'Download',
    'common.replace': 'Replace',
    'common.back': 'Back',
    'common.actions': 'Actions',
    'common.close': 'Close',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.viewAll': 'View all',
    'common.search': 'Search',
    'common.optional': 'Optional',
    'common.required': 'Required',
    'common.filename': 'Filename',
    'common.size': 'Size',
    'common.uploaded': 'Uploaded',
    'common.notProvided': 'Not provided',
    'common.unknown': 'Unknown',
    'common.confirmRemove': 'Remove {filename}?',

    // ── Layout / Nav ───────────────────────────────────────────────────
    'layout.brandSuffix': 'MPCA · MPL Finance Console',
    'layout.divisionPortal': 'Division Portal',
    'layout.logout': 'Logout',
    'layout.loadingPortal': 'Loading portal…',
    'layout.noDivisionLink': "Your account isn't linked to a division. Please contact MPL accounts.",
    'layout.helpTitle': 'Secretary Guide',
    'layout.helpBlurb': 'A short illustrated walkthrough of every portal screen.',
    'layout.helpDownload': 'Download PDF',
    'nav.dashboard': 'Dashboard',
    'nav.myClaims': 'My Claims',
    'nav.kyc': 'KYC Documents',
    'lang.toggle': 'हिन्दी',
    'lang.tooltip': 'Switch language',

    // ── Dashboard ─────────────────────────────────────────────────────
    'dash.welcome': 'Welcome, {name}',
    'dash.subtitle': "Submit grant claims, track their status, and keep your division's KYC up to date.",
    'dash.viewMyClaims': 'View my claims',
    'dash.tile.activeGrants': 'Active Grants',
    'dash.tile.totalClaimed': 'Total Claimed',
    'dash.tile.received': 'Received',
    'dash.tile.kycDocs': 'KYC Documents',
    'dash.section.claimStatus': 'Claim status',
    'dash.section.myClaims': 'My Claims',
    'dash.section.myClaimsHint': 'Click a claim to see details, attachments and status timeline.',
    'dash.section.availablePolicies': 'Available Grant Policies',
    'dash.noPolicies': "No active grant policies right now. You'll be notified when one is published.",
    'dash.eligible': 'eligible',
    'dash.requiredDocsCount': '{n} required doc(s)',
    'dash.startClaim': 'Start claim',
    'dash.docs': '{n} doc(s)',
    'dash.submittedOn': 'Submitted {date}',

    // ── Claims list / detail ──────────────────────────────────────────
    'claims.title': 'My Claims',
    'claims.newClaim': 'New Claim',
    'claims.empty': 'No claims yet. Click "New Claim" to start one.',
    'claims.table.policy': 'Policy',
    'claims.table.fy': 'Fiscal Year',
    'claims.table.amount': 'Amount',
    'claims.table.docs': 'Documents',
    'claims.table.status': 'Status',
    'claims.table.submittedAt': 'Submitted',
    'claims.col.action': '',
    'claims.openDetail': 'Open',
    'claims.backToList': 'Back to claims',

    'claims.dialog.create.title': 'New Grant Claim',
    'claims.dialog.create.policy': 'Grant Policy',
    'claims.dialog.create.amount': 'Claim Amount (₹)',
    'claims.dialog.create.maxEligible': 'Maximum eligible: ₹{amount}',
    'claims.dialog.create.amountHint': 'Enter the amount you want to claim here',
    'claims.detail.editAmount': 'Edit amount',
    'claims.detail.saveAmount': 'Save',
    'claims.detail.cancelEdit': 'Cancel',
    'claims.detail.amountSaved': 'Claim amount updated',
    'claims.detail.amountTooHigh': 'Cannot exceed eligible cap of ₹{amount}',
    'claims.card.docsLabel': 'documents',
    'claims.card.docLabel': 'document',
    'claims.card.delete': 'Delete',
    'claims.card.open': 'Open',
    'claims.delete.title': 'Delete this draft claim?',
    'claims.delete.body': 'This permanently removes the draft and any documents you have uploaded for it. This cannot be undone.',
    'claims.delete.confirm': 'Yes, delete',
    'claims.delete.cancel': 'Keep it',
    'claims.delete.success': 'Draft claim deleted',
    'claims.preSubmit.title': 'Review before submission',
    'claims.preSubmit.subtitle': "AI re-verified your claim. Review the verdict, then confirm to send it to MPL.",
    'claims.preSubmit.running': 'Running AI review (≈15s)…',
    'claims.preSubmit.aiFailed': 'AI review failed — you can still confirm',
    'claims.preSubmit.submitting': 'Submitting…',
    'claims.preSubmit.submitted': 'Claim submitted',
    'claims.preSubmit.resubmitted': 'Claim resubmitted',
    'claims.preSubmit.submitFailed': 'Submit failed',
    'claims.preSubmit.confirm': 'Confirm submission',
    'claims.preSubmit.cancel': 'Cancel — let me fix things',
    'claims.preSubmit.score': 'AI satisfaction score',
    'claims.preSubmit.missing': 'Documents not covered',
    'claims.preSubmit.allCovered': 'All required documents are present',
    'claims.preSubmit.perDoc': 'Per-document verdict',
    'claims.preSubmit.none': 'No documents reviewed yet',
    'claims.preSubmit.scoreInfo': 'A low score does not block submission — it just lets you decide whether to fix things first.',
    'claims.dialog.create.notes': 'Notes (optional)',
    'claims.dialog.create.notesPlaceholder': "What is the grant being claimed for? (e.g., 'Cricket nets for under-14 academy')",
    'claims.dialog.create.submit': 'Create claim',
    'claims.dialog.create.submitting': 'Creating…',
    'claims.dialog.create.amountExceedsEligible': 'Claim amount cannot exceed eligible amount of ₹{amount}',
    'claims.dialog.create.pickPolicy': 'Pick a policy first',

    // Side sheet / claim detail
    'claims.detail.title': 'Claim Details',
    'claims.detail.policy': 'Grant policy',
    'claims.detail.claimAmount': 'Claim amount',
    'claims.detail.approvedAmount': 'Approved amount',
    'claims.detail.approvedBanner': 'You claimed ₹{claimed} → Approved ₹{approved}',
    'claims.detail.approvedNote': 'Note from MPL accounts',
    'claims.detail.editClaim': 'Edit claim',
    'claims.detail.deleteClaim': 'Delete claim',
    'claims.detail.confirmDelete': 'Delete this draft claim? Uploaded files will be removed too.',
    'claims.detail.section.docs': 'Documents',
    'claims.detail.section.docsHint': 'Upload each required document below. AI will review every file as soon as you upload it.',
    'claims.detail.section.timeline': 'Status timeline',
    'claims.detail.section.aiReview': 'AI review',
    'claims.detail.aiScore': 'Satisfaction score',
    'claims.detail.aiComments': 'AI comments',
    'claims.detail.aiMissing': 'AI says these are missing:',
    'claims.detail.aiRecommended': 'AI-recommended amount',
    'claims.detail.runAi': 'Run AI Review',
    'claims.detail.rerunAi': 'Re-run AI',
    'claims.detail.aiRunning': 'AI is reviewing…',
    'claims.detail.submit': 'Submit for review',
    'claims.detail.submitting': 'Submitting…',
    'claims.detail.submitBlocked': "Still required ({n}) — submit is blocked",
    'claims.detail.submitBlockedHint': 'Upload these to unblock submission:',
    'claims.detail.submitted': 'Submitted',
    'claims.detail.resubmit': 'Resubmit',
    'claims.detail.sentBackBanner': 'Sent back by MPL accounts — please address the comments and resubmit.',
    'claims.detail.snapshotBanner': 'Showing the snapshot at the time of last submission.',

    // ── Status names ─────────────────────────────────────────────────
    'status.draft': 'Draft',
    'status.submitted': 'Submitted',
    'status.in_process': 'In Process',
    'status.approved': 'Approved',
    'status.payment_initiated': 'Payment Initiated',
    'status.paid': 'Paid',
    'status.sent_back': 'Sent Back',

    // ── Upload UX (RequiredDocsUploader + Checklist) ─────────────────
    'docs.expected': 'Expected',
    'docs.checklistHeading': 'What we expect in this document',
    'docs.mpcaDisclaimer': 'Note: This document is subject to final approval by MPCA authorities.',
    'docs.uploadedBadge': 'Uploaded',
    'docs.pendingBadge': 'Pending',
    'docs.missingBadge': 'Missing',
    'docs.extraBadge': 'Extra — not required',
    'docs.extraByPolicyBadge': 'Extra — not required by policy',
    'docs.aiPending': 'AI pending',
    'docs.aiProcessing': 'AI processing — please wait',
    'docs.uploadBlockedAi': 'Wait for AI to finish reviewing the current document',
    'docs.accepted': 'Accepted',
    'docs.rejected': 'Rejected',
    'docs.wrongSlot': 'Wrong slot',
    'docs.wrongSlugFits': 'Wrong slug — fits {slug}',
    'docs.aiObservation': 'AI Observation',
    'docs.aiRecommendation': 'AI Recommendation',
    'docs.noFile': 'No file uploaded for this requirement.',
    'docs.noDocsYet': 'No documents yet.',
    'docs.extraUploads': 'Extra uploads',
    'docs.additionalUploads': 'Additional uploads',

    // Built-in doc type labels (slugs → friendly names)
    'doctype.claim_letter': 'Claim Letter',
    'doctype.invoice': 'Invoice / Bill',
    'doctype.bank_proof': 'Bank Proof',
    'doctype.other': 'Other',
    'doctype.utilization_certificate': 'Utilization Certificate',
    'doctype.completion_report': 'Completion Report',
    'doctype.photographs': 'Photographs',
    'doctype.quotation': 'Quotation',
    'doctype.gst_certificate': 'GST Certificate',
    'doctype.pan_card': 'PAN Card',
    'doctype.msme_certificate': 'MSME Certificate',
    'doctype.cancelled_cheque': 'Cancelled Cheque',
    'doctype.proposal_letter': 'Proposal Letter',
    'doctype.cost_estimate_quotation': 'Cost Estimate / Quotation',
    'doctype.timeline_plan': 'Timeline Plan',
    'doctype.committee_resolution': 'Committee Resolution',
    'doctype.authorisation_letter': 'Authorisation Letter',
    'doctype.bank_authorisation_letter': 'Bank Authorisation Letter',
    'doctype.no_dues_certificate': 'No-Dues Certificate',

    // ── KYC ───────────────────────────────────────────────────────────
    'kyc.title': 'KYC & Banking',
    'kyc.subtitle': "Keep your division's banking and tax details up to date — MPL accounts uses them to process grant payments.",
    'kyc.profileTitle': 'Division Profile',
    'kyc.profileDesc': "Name, code and email are managed by MPL accounts and can't be edited here.",
    'kyc.field.name': 'Name',
    'kyc.field.code': 'Code',
    'kyc.field.contactEmail': 'Contact Email',
    'kyc.field.contactPhone': 'Contact Phone',
    'kyc.field.pan': 'PAN',
    'kyc.field.gstin': 'GSTIN',
    'kyc.field.bankAccount': 'Bank Account #',
    'kyc.field.ifsc': 'IFSC Code',
    'kyc.field.address': 'Address',
    'kyc.saved': 'Division details saved',
    'kyc.saveFailed': 'Save failed',
    'kyc.docsTitle': 'KYC Documents',
    'kyc.docsDesc': 'Upload GST certificate, PAN card, MSME certificate and a cancelled cheque.',
    'kyc.uploaded': 'KYC document uploaded',
    'kyc.uploadFailed': 'Upload failed',
    'kyc.deleteFailed': 'Delete failed',
    'kyc.downloadFailed': 'Download failed',
    'kyc.removed': 'Removed',
    'kyc.empty': 'No KYC documents yet. Pick a category and click ',
    'kyc.cat.gst_certificate': 'GST Certificate',
    'kyc.cat.pan_card': 'PAN Card',
    'kyc.cat.msme_certificate': 'MSME Certificate',
    'kyc.cat.cancelled_cheque': 'Cancelled Cheque',
    'kyc.cat.other': 'Other',

    // ── Policy detail (read-only secretary view) ─────────────────────
    'policy.title': 'Grant Policy',
    'policy.fiscalYear': 'Fiscal Year',
    'policy.eligibleAmount': 'Eligible Amount',
    'policy.description': 'Description',
    'policy.requiredDocs': 'Required Documents',
    'policy.policyDocument': 'Policy Document',
    'policy.startClaimHere': 'Start a claim',
    'policy.viewClaims': 'View my claims for this policy',
    'policy.descriptionFor': "What to upload",
    'policy.backDashboard': 'Back to dashboard',
    'policy.statusActive': 'Active',
    'policy.statusClosed': 'Closed',
    'policy.readOnly': 'Read only',
    'policy.schemeDetails': 'Scheme details',
    'policy.eligiblePerDivision': 'Eligible per division',
    'policy.aboutScheme': 'About this scheme',
    'policy.docsToAttach': 'Documents to attach',
    'policy.sampleToFillSign': 'Sample to fill & sign',
    'policy.downloadSchemePdf': 'Download scheme PDF',
    'policy.startClaimAgainstScheme': 'Start a claim against this scheme',
    'policy.loadingPolicy': 'Loading policy…',
  },

  hi: {
    // ── Common ─────────────────────────────────────────────────────────
    'common.loading': 'लोड हो रहा है…',
    'common.save': 'सहेजें',
    'common.saving': 'सहेजा जा रहा है…',
    'common.cancel': 'रद्द करें',
    'common.delete': 'हटाएँ',
    'common.remove': 'हटाएँ',
    'common.upload': 'अपलोड करें',
    'common.uploading': 'अपलोड हो रहा है…',
    'common.preview': 'पूर्वावलोकन',
    'common.download': 'डाउनलोड',
    'common.replace': 'बदलें',
    'common.back': 'वापस',
    'common.actions': 'क्रियाएँ',
    'common.close': 'बंद करें',
    'common.yes': 'हाँ',
    'common.no': 'नहीं',
    'common.viewAll': 'सभी देखें',
    'common.search': 'खोजें',
    'common.optional': 'वैकल्पिक',
    'common.required': 'अनिवार्य',
    'common.filename': 'फ़ाइल का नाम',
    'common.size': 'आकार',
    'common.uploaded': 'अपलोड किया',
    'common.notProvided': 'दिया नहीं गया',
    'common.unknown': 'अज्ञात',
    'common.confirmRemove': 'क्या {filename} हटाएँ?',

    // ── Layout / Nav ───────────────────────────────────────────────────
    'layout.brandSuffix': 'एमपीसीए · MPL वित्त कंसोल',
    'layout.divisionPortal': 'डिवीज़न पोर्टल',
    'layout.logout': 'लॉग आउट',
    'layout.loadingPortal': 'पोर्टल लोड हो रहा है…',
    'layout.noDivisionLink': 'आपका खाता किसी डिवीज़न से नहीं जुड़ा है। कृपया MPL एकाउंट्स से संपर्क करें।',
    'layout.helpTitle': 'सचिव मार्गदर्शिका',
    'layout.helpBlurb': 'पोर्टल के हर स्क्रीन का संक्षिप्त सचित्र विवरण।',
    'layout.helpDownload': 'PDF डाउनलोड करें',
    'nav.dashboard': 'डैशबोर्ड',
    'nav.myClaims': 'मेरे दावे',
    'nav.kyc': 'केवाईसी दस्तावेज़',
    'lang.toggle': 'English',
    'lang.tooltip': 'भाषा बदलें',

    // ── Dashboard ─────────────────────────────────────────────────────
    'dash.welcome': 'स्वागत है, {name}',
    'dash.subtitle': 'अनुदान दावे जमा करें, उनकी स्थिति देखें, और अपने डिवीज़न का केवाईसी अद्यतन रखें।',
    'dash.viewMyClaims': 'मेरे दावे देखें',
    'dash.tile.activeGrants': 'सक्रिय अनुदान',
    'dash.tile.totalClaimed': 'कुल दावा',
    'dash.tile.received': 'प्राप्त',
    'dash.tile.kycDocs': 'केवाईसी दस्तावेज़',
    'dash.section.claimStatus': 'दावा स्थिति',
    'dash.section.myClaims': 'मेरे दावे',
    'dash.section.myClaimsHint': 'किसी दावे पर क्लिक करके विवरण, अनुलग्नक और स्थिति देखें।',
    'dash.section.availablePolicies': 'उपलब्ध अनुदान योजनाएँ',
    'dash.noPolicies': 'फिलहाल कोई सक्रिय अनुदान योजना नहीं है। नई योजना प्रकाशित होने पर आपको सूचित किया जाएगा।',
    'dash.eligible': 'पात्र',
    'dash.requiredDocsCount': '{n} अनिवार्य दस्तावेज़',
    'dash.startClaim': 'दावा शुरू करें',
    'dash.docs': '{n} दस्तावेज़',
    'dash.submittedOn': '{date} को जमा किया',

    // ── Claims list / detail ──────────────────────────────────────────
    'claims.title': 'मेरे दावे',
    'claims.newClaim': 'नया दावा',
    'claims.empty': 'अभी कोई दावा नहीं है। "नया दावा" क्लिक करके शुरू करें।',
    'claims.table.policy': 'योजना',
    'claims.table.fy': 'वित्त वर्ष',
    'claims.table.amount': 'राशि',
    'claims.table.docs': 'दस्तावेज़',
    'claims.table.status': 'स्थिति',
    'claims.table.submittedAt': 'जमा तिथि',
    'claims.col.action': '',
    'claims.openDetail': 'खोलें',
    'claims.backToList': 'दावों पर लौटें',

    'claims.dialog.create.title': 'नया अनुदान दावा',
    'claims.dialog.create.policy': 'अनुदान योजना',
    'claims.dialog.create.amount': 'दावा राशि (₹)',
    'claims.dialog.create.maxEligible': 'अधिकतम पात्र: ₹{amount}',
    'claims.dialog.create.amountHint': 'यहाँ वह राशि दर्ज करें जिसका आप दावा करना चाहते हैं',
    'claims.detail.editAmount': 'राशि संपादित करें',
    'claims.detail.saveAmount': 'सहेजें',
    'claims.detail.cancelEdit': 'रद्द करें',
    'claims.detail.amountSaved': 'दावा राशि अपडेट हो गई',
    'claims.detail.amountTooHigh': 'पात्रता सीमा ₹{amount} से अधिक नहीं हो सकती',
    'claims.card.docsLabel': 'दस्तावेज़',
    'claims.card.docLabel': 'दस्तावेज़',
    'claims.card.delete': 'हटाएँ',
    'claims.card.open': 'खोलें',
    'claims.delete.title': 'क्या इस ड्राफ्ट दावे को हटाना है?',
    'claims.delete.body': 'इससे ड्राफ्ट और उससे अपलोड किए गए सभी दस्तावेज़ स्थायी रूप से हट जाएँगे। यह क्रिया वापस नहीं हो सकती।',
    'claims.delete.confirm': 'हाँ, हटाएँ',
    'claims.delete.cancel': 'रहने दें',
    'claims.delete.success': 'ड्राफ्ट दावा हटा दिया गया',
    'claims.preSubmit.title': 'सबमिशन से पहले समीक्षा',
    'claims.preSubmit.subtitle': 'AI ने आपके दावे की पुनः जाँच की है। परिणाम देखें और फिर MPL को भेजने की पुष्टि करें।',
    'claims.preSubmit.running': 'AI समीक्षा चल रही है (≈15 सेकंड)…',
    'claims.preSubmit.aiFailed': 'AI समीक्षा विफल — आप फिर भी पुष्टि कर सकते हैं',
    'claims.preSubmit.submitting': 'सबमिट हो रहा है…',
    'claims.preSubmit.submitted': 'दावा सबमिट हो गया',
    'claims.preSubmit.resubmitted': 'दावा पुनः सबमिट हो गया',
    'claims.preSubmit.submitFailed': 'सबमिट विफल',
    'claims.preSubmit.confirm': 'सबमिशन की पुष्टि करें',
    'claims.preSubmit.cancel': 'रद्द करें — मैं सुधार करूँगा',
    'claims.preSubmit.score': 'AI संतुष्टि स्कोर',
    'claims.preSubmit.missing': 'अनुपस्थित दस्तावेज़',
    'claims.preSubmit.allCovered': 'सभी अनिवार्य दस्तावेज़ उपलब्ध हैं',
    'claims.preSubmit.perDoc': 'प्रति-दस्तावेज़ निर्णय',
    'claims.preSubmit.none': 'अभी तक कोई दस्तावेज़ समीक्षित नहीं',
    'claims.preSubmit.scoreInfo': 'कम स्कोर सबमिशन को नहीं रोकता — यह केवल आपको पहले सुधार करने का अवसर देता है।',
    'claims.dialog.create.notes': 'टिप्पणी (वैकल्पिक)',
    'claims.dialog.create.notesPlaceholder': 'अनुदान किस उद्देश्य के लिए माँगा जा रहा है? (उदा: "अंडर-14 अकादमी हेतु क्रिकेट नेट")',
    'claims.dialog.create.submit': 'दावा बनाएँ',
    'claims.dialog.create.submitting': 'बनाया जा रहा है…',
    'claims.dialog.create.amountExceedsEligible': 'दावा राशि पात्र राशि ₹{amount} से अधिक नहीं हो सकती',
    'claims.dialog.create.pickPolicy': 'पहले एक योजना चुनें',

    'claims.detail.title': 'दावा विवरण',
    'claims.detail.policy': 'अनुदान योजना',
    'claims.detail.claimAmount': 'दावा राशि',
    'claims.detail.approvedAmount': 'स्वीकृत राशि',
    'claims.detail.approvedBanner': 'आपका दावा ₹{claimed} → स्वीकृत ₹{approved}',
    'claims.detail.approvedNote': 'MPL एकाउंट्स की टिप्पणी',
    'claims.detail.editClaim': 'दावा संपादित करें',
    'claims.detail.deleteClaim': 'दावा हटाएँ',
    'claims.detail.confirmDelete': 'क्या यह ड्राफ़्ट दावा हटाएँ? अपलोड की गई फ़ाइलें भी हट जाएँगी।',
    'claims.detail.section.docs': 'दस्तावेज़',
    'claims.detail.section.docsHint': 'नीचे प्रत्येक अनिवार्य दस्तावेज़ अपलोड करें। अपलोड होते ही AI तुरंत समीक्षा करेगा।',
    'claims.detail.section.timeline': 'स्थिति समयरेखा',
    'claims.detail.section.aiReview': 'AI समीक्षा',
    'claims.detail.aiScore': 'संतुष्टि स्कोर',
    'claims.detail.aiComments': 'AI टिप्पणियाँ',
    'claims.detail.aiMissing': 'AI के अनुसार ये दस्तावेज़ अनुपस्थित हैं:',
    'claims.detail.aiRecommended': 'AI द्वारा अनुशंसित राशि',
    'claims.detail.runAi': 'AI समीक्षा चलाएँ',
    'claims.detail.rerunAi': 'AI फिर चलाएँ',
    'claims.detail.aiRunning': 'AI समीक्षा कर रहा है…',
    'claims.detail.submit': 'समीक्षा हेतु जमा करें',
    'claims.detail.submitting': 'जमा हो रहा है…',
    'claims.detail.submitBlocked': 'अभी भी अनिवार्य ({n}) — जमा करना रुका है',
    'claims.detail.submitBlockedHint': 'जमा खोलने के लिए ये अपलोड करें:',
    'claims.detail.submitted': 'जमा कर दिया गया',
    'claims.detail.resubmit': 'पुनः जमा करें',
    'claims.detail.sentBackBanner': 'MPL एकाउंट्स ने वापस भेजा है — कृपया टिप्पणियों का समाधान कर पुनः जमा करें।',
    'claims.detail.snapshotBanner': 'अंतिम सबमिशन के समय का स्नैपशॉट दिखाया जा रहा है।',

    // ── Status names ─────────────────────────────────────────────────
    'status.draft': 'ड्राफ़्ट',
    'status.submitted': 'जमा',
    'status.in_process': 'प्रक्रियाधीन',
    'status.approved': 'स्वीकृत',
    'status.payment_initiated': 'भुगतान आरंभ',
    'status.paid': 'भुगतान पूर्ण',
    'status.sent_back': 'वापस भेजा',

    // ── Upload UX ────────────────────────────────────────────────────
    'docs.expected': 'अपेक्षित',
    'docs.checklistHeading': 'इस दस्तावेज़ में हमें क्या अपेक्षित है',
    'docs.mpcaDisclaimer': 'सूचना: यह दस्तावेज़ MPCA अधिकारियों की अंतिम स्वीकृति के अधीन है।',
    'docs.uploadedBadge': 'अपलोड हुआ',
    'docs.pendingBadge': 'लंबित',
    'docs.missingBadge': 'अनुपस्थित',
    'docs.extraBadge': 'अतिरिक्त — अनिवार्य नहीं',
    'docs.extraByPolicyBadge': 'अतिरिक्त — योजना द्वारा अनिवार्य नहीं',
    'docs.aiPending': 'AI लंबित',
    'docs.aiProcessing': 'AI जाँच रही है — कृपया प्रतीक्षा करें',
    'docs.uploadBlockedAi': 'वर्तमान दस्तावेज़ की AI समीक्षा पूरी होने तक प्रतीक्षा करें',
    'docs.accepted': 'स्वीकृत',
    'docs.rejected': 'अस्वीकृत',
    'docs.wrongSlot': 'गलत स्थान',
    'docs.wrongSlugFits': 'गलत स्लग — यह {slug} के लिए उपयुक्त है',
    'docs.aiObservation': 'AI निरीक्षण',
    'docs.aiRecommendation': 'AI सिफ़ारिश',
    'docs.noFile': 'इस अनिवार्यता हेतु कोई फ़ाइल अपलोड नहीं की गई है।',
    'docs.noDocsYet': 'अभी कोई दस्तावेज़ नहीं है।',
    'docs.extraUploads': 'अतिरिक्त अपलोड',
    'docs.additionalUploads': 'अतिरिक्त अपलोड',

    // Doc type labels (slugs)
    'doctype.claim_letter': 'दावा पत्र',
    'doctype.invoice': 'चालान / बिल',
    'doctype.bank_proof': 'बैंक प्रमाण',
    'doctype.other': 'अन्य',
    'doctype.utilization_certificate': 'उपयोगिता प्रमाणपत्र',
    'doctype.completion_report': 'पूर्णता रिपोर्ट',
    'doctype.photographs': 'फ़ोटोग्राफ़',
    'doctype.quotation': 'कोटेशन',
    'doctype.gst_certificate': 'जीएसटी प्रमाणपत्र',
    'doctype.pan_card': 'पैन कार्ड',
    'doctype.msme_certificate': 'एमएसएमई प्रमाणपत्र',
    'doctype.cancelled_cheque': 'रद्द किया गया चेक',
    'doctype.proposal_letter': 'प्रस्ताव पत्र',
    'doctype.cost_estimate_quotation': 'लागत अनुमान / कोटेशन',
    'doctype.timeline_plan': 'समय-सीमा योजना',
    'doctype.committee_resolution': 'समिति प्रस्ताव',
    'doctype.authorisation_letter': 'प्राधिकरण पत्र',
    'doctype.bank_authorisation_letter': 'बैंक प्राधिकरण पत्र',
    'doctype.no_dues_certificate': 'बकाया-नहीं प्रमाणपत्र',

    // ── KYC ───────────────────────────────────────────────────────────
    'kyc.title': 'केवाईसी और बैंकिंग',
    'kyc.subtitle': 'अपने डिवीज़न की बैंकिंग और कर संबंधी जानकारी अद्यतन रखें — MPL एकाउंट्स इसी के आधार पर अनुदान भुगतान करता है।',
    'kyc.profileTitle': 'डिवीज़न प्रोफ़ाइल',
    'kyc.profileDesc': 'नाम, कोड और ईमेल MPL एकाउंट्स द्वारा प्रबंधित होते हैं और यहाँ संपादित नहीं किए जा सकते।',
    'kyc.field.name': 'नाम',
    'kyc.field.code': 'कोड',
    'kyc.field.contactEmail': 'संपर्क ईमेल',
    'kyc.field.contactPhone': 'संपर्क फ़ोन',
    'kyc.field.pan': 'पैन',
    'kyc.field.gstin': 'जीएसटीआईएन',
    'kyc.field.bankAccount': 'बैंक खाता क्रमांक',
    'kyc.field.ifsc': 'आईएफ़एससी कोड',
    'kyc.field.address': 'पता',
    'kyc.saved': 'डिवीज़न विवरण सहेजे गए',
    'kyc.saveFailed': 'सहेजना विफल',
    'kyc.docsTitle': 'केवाईसी दस्तावेज़',
    'kyc.docsDesc': 'जीएसटी प्रमाणपत्र, पैन कार्ड, एमएसएमई प्रमाणपत्र और एक रद्द चेक अपलोड करें।',
    'kyc.uploaded': 'केवाईसी दस्तावेज़ अपलोड हुआ',
    'kyc.uploadFailed': 'अपलोड विफल',
    'kyc.deleteFailed': 'हटाना विफल',
    'kyc.downloadFailed': 'डाउनलोड विफल',
    'kyc.removed': 'हटा दिया गया',
    'kyc.empty': 'अभी कोई केवाईसी दस्तावेज़ नहीं है। श्रेणी चुनकर ',
    'kyc.cat.gst_certificate': 'जीएसटी प्रमाणपत्र',
    'kyc.cat.pan_card': 'पैन कार्ड',
    'kyc.cat.msme_certificate': 'एमएसएमई प्रमाणपत्र',
    'kyc.cat.cancelled_cheque': 'रद्द किया गया चेक',
    'kyc.cat.other': 'अन्य',

    // ── Policy detail ────────────────────────────────────────────────
    'policy.title': 'अनुदान योजना',
    'policy.fiscalYear': 'वित्त वर्ष',
    'policy.eligibleAmount': 'पात्र राशि',
    'policy.description': 'विवरण',
    'policy.requiredDocs': 'अनिवार्य दस्तावेज़',
    'policy.policyDocument': 'योजना दस्तावेज़',
    'policy.startClaimHere': 'दावा शुरू करें',
    'policy.viewClaims': 'इस योजना के मेरे दावे देखें',
    'policy.descriptionFor': 'क्या अपलोड करें',
    'policy.backDashboard': 'डैशबोर्ड पर लौटें',
    'policy.statusActive': 'सक्रिय',
    'policy.statusClosed': 'बंद',
    'policy.readOnly': 'केवल पढ़ें',
    'policy.schemeDetails': 'योजना विवरण',
    'policy.eligiblePerDivision': 'प्रति डिवीज़न पात्रता',
    'policy.aboutScheme': 'इस योजना के बारे में',
    'policy.docsToAttach': 'संलग्न करने हेतु दस्तावेज़',
    'policy.sampleToFillSign': 'भरें व हस्ताक्षर करने हेतु नमूना',
    'policy.downloadSchemePdf': 'योजना PDF डाउनलोड करें',
    'policy.startClaimAgainstScheme': 'इस योजना के लिए दावा शुरू करें',
    'policy.loadingPolicy': 'योजना लोड हो रही है…',
  },
};
